import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { sankey, sankeyLinkHorizontal, sankeyLeft } from 'd3-sankey'
import WarehouseInventoryWaterfall from '@/components/WarehouseInventoryWaterfall'
import type {
  WarehouseBkMatchItem,
  WarehouseInvoiceSummary,
  WarehouseLedgerTransactionItem,
} from '@/services/warehouseTypes'
import {
  buildPeriodFlowModel,
  buildProductNameMapFromLedger,
  catalogStockToFlowRows,
  collectLedgerFlowMonths,
  drawMetric,
  filterLedgerInflowsForMonth,
  filterLedgerOutflowsForMonth,
  filterActiveLedgerRows,
  flowMetric,
  isLedgerInflow,
  isLedgerOutflow,
  summarizeLedgerAdjustments,
  type CatalogStockRow,
  type FlowMode,
  type FlowPeriodSkuRow,
  type FlowScope,
} from '@/utils/ledgerFlow'

type FlowChartView = 'waterfall' | 'sankey'

type SourceKind = 'purchases' | 'prior_stock'

type SankeyNodeExtra = {
  name: string
  nodeId: number
  color?: string
  column: 'source' | 'warehouse' | 'store'
  bulkSku?: string
  sourceKind?: SourceKind | 'store'
  /** Hide text label (warehouse passthrough nodes). */
  hideLabel?: boolean
}

type SankeyLinkInput = {
  source: number
  target: number
  value: number
  color?: string
  /** Tooltip line, e.g. "INV-123 - 40". */
  label?: string
  bulkSku?: string
  sourceKind?: SourceKind | 'store'
  unitCount?: number
}

type HoverTip = {
  label: string
  bulkSku: string
  x: number
  y: number
}

const SKU_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
]

const MIN_WIDTH = 280
const DEFAULT_WIDTH = 720
const MIN_CHART_HEIGHT = 600
const LEFT_MARGIN = 200
const RIGHT_MARGIN = 100
const NODE_WIDTH = 12
/** Extra vertical gap between store nodes on the right axis. */
const STORE_GAP = 40

function formatMoney(value: number): string {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function formatQty(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

function skuDisplayLabel(
  bulkSku: string,
  product?: string | null,
  displayNames?: Map<string, string>
): string {
  const crosswalkName = displayNames?.get(bulkSku)?.trim()
  const name = crosswalkName || product?.trim()
  return name ? `${name} (${bulkSku})` : bulkSku
}

function buildSkuColorMap(skus: string[]): Map<string, string> {
  const sorted = [...skus].sort((a, b) => a.localeCompare(b))
  const map = new Map<string, string>()
  sorted.forEach((sku, index) => {
    map.set(sku, SKU_COLORS[index % SKU_COLORS.length] ?? '#64748b')
  })
  return map
}

/**
 * Movement Sankey: Purchases (or prior stock) → Warehouse → Stores.
 * Opening and ending inventory are shown in the waterfall view only.
 */
function buildMovementFlowGraph(
  model: FlowPeriodSkuRow[],
  mode: FlowMode,
  scope: FlowScope,
  productNames: Map<string, string>,
  skuColorMap: Map<string, string>
): { nodes: SankeyNodeExtra[]; links: SankeyLinkInput[] } {
  const nodes: SankeyNodeExtra[] = []
  const links: SankeyLinkInput[] = []

  const nodeIndex = new Map<string, number>()
  const ensureNode = (key: string, extras: Omit<SankeyNodeExtra, 'nodeId'>) => {
    const existing = nodeIndex.get(key)
    if (existing != null) return existing
    const id = nodes.length
    nodes.push({ ...extras, nodeId: id })
    nodeIndex.set(key, id)
    return id
  }

  for (const row of model) {
    const sku = row.bulk_sku
    const color = skuColorMap.get(sku) ?? '#64748b'
    const skuLabel = skuDisplayLabel(sku, productNames.get(sku) ?? null)

    const purchasesMetric = flowMetric(row.purchases, mode)
    const totalDrawMetric = row.storeDraws.reduce(
      (sum, draw) => sum + drawMetric(draw, mode),
      0
    )

    if (purchasesMetric <= 0 && totalDrawMetric <= 0) continue

    const priorStockMetric =
      scope === 'month' && totalDrawMetric > purchasesMetric
        ? totalDrawMetric - purchasesMetric
        : 0

    const midId = ensureNode(`wh:${sku}`, {
      name: skuLabel,
      column: 'warehouse',
      color,
      bulkSku: sku,
      hideLabel: true,
    })

    if (purchasesMetric > 0) {
      const sourceId = ensureNode(`src:purchases:${sku}`, {
        name: `Purchases · ${skuLabel}`,
        column: 'source',
        color,
        bulkSku: sku,
        sourceKind: 'purchases',
      })
      links.push({
        source: sourceId,
        target: midId,
        value: purchasesMetric,
        color,
        label: `Purchases: ${formatQty(row.purchases.qty)} cases`,
        bulkSku: sku,
        sourceKind: 'purchases',
        unitCount: row.purchases.qty,
      })
    }

    if (priorStockMetric > 0) {
      const sourceId = ensureNode(`src:prior:${sku}`, {
        name: `Prior stock · ${skuLabel}`,
        column: 'source',
        color,
        bulkSku: sku,
        sourceKind: 'prior_stock',
      })
      links.push({
        source: sourceId,
        target: midId,
        value: priorStockMetric,
        color,
        label:
          mode === 'qty'
            ? `Prior stock: ${formatQty(priorStockMetric)} cases`
            : `Prior stock: ${formatMoney(priorStockMetric)}`,
        bulkSku: sku,
        sourceKind: 'prior_stock',
        unitCount: mode === 'qty' ? priorStockMetric : undefined,
      })
    }

    for (const draw of row.storeDraws) {
      const drawValue = drawMetric(draw, mode)
      if (drawValue <= 0) continue

      const storeId = ensureNode(`store:${draw.destination}`, {
        name: draw.destination,
        column: 'store',
        color: '#475569',
        sourceKind: 'store',
      })
      links.push({
        source: midId,
        target: storeId,
        value: drawValue,
        color,
        label: `${draw.destination} - ${formatQty(draw.qty)} cases`,
        bulkSku: sku,
        sourceKind: 'store',
        unitCount: draw.qty,
      })
    }
  }

  return { nodes, links }
}

type LayoutNode = SankeyNodeExtra & {
  x0?: number
  x1?: number
  y0?: number
  y1?: number
  targetLinks?: Array<{ y0?: number; y1?: number }>
}

/** Pin columns to fixed x positions; space & vertically center store nodes. */
function pinColumns(
  nodes: LayoutNode[],
  chartLeft: number,
  chartRight: number,
  chartTop: number,
  chartBottom: number
) {
  const midX = (chartLeft + chartRight - NODE_WIDTH) / 2
  const storeX = chartRight - NODE_WIDTH

  for (const node of nodes) {
    const width = Math.max(NODE_WIDTH, (node.x1 ?? 0) - (node.x0 ?? 0))
    if (node.column === 'source') {
      node.x0 = chartLeft
      node.x1 = chartLeft + width
    } else if (node.column === 'warehouse') {
      node.x0 = midX
      node.x1 = midX + width
    } else {
      node.x0 = storeX
      node.x1 = storeX + width
    }
  }

  const stores = nodes
    .filter((node) => node.column === 'store')
    .sort((a, b) => (a.y0 ?? 0) - (b.y0 ?? 0))

  if (stores.length === 0) return

  const heights = stores.map((node) => Math.max(1, (node.y1 ?? 0) - (node.y0 ?? 0)))
  const blockHeight =
    heights.reduce((sum, h) => sum + h, 0) + STORE_GAP * Math.max(0, stores.length - 1)
  const chartMid = (chartTop + chartBottom) / 2
  let y = chartMid - blockHeight / 2

  for (let i = 0; i < stores.length; i++) {
    const node = stores[i]
    if (!node) continue
    const height = heights[i] ?? 1
    const dy = y - (node.y0 ?? 0)
    node.y0 = y
    node.y1 = y + height

    for (const link of node.targetLinks ?? []) {
      link.y1 = (link.y1 ?? 0) + dy
    }

    y = (node.y1 ?? y) + STORE_GAP
  }
}

type WarehouseInventoryFlowProps = {
  ledgerRows: WarehouseLedgerTransactionItem[]
  catalogStock: CatalogStockRow[]
  invoices?: WarehouseInvoiceSummary[]
  bkMatches?: WarehouseBkMatchItem[]
  availableMonths: string[]
  formatMonthLabel: (month: string) => string
  skuDisplayNames?: Map<string, string>
  productInvoiceNumberByLineId?: Map<number, string>
}

export default function WarehouseInventoryFlow({
  ledgerRows,
  catalogStock,
  availableMonths,
  formatMonthLabel,
  skuDisplayNames,
}: WarehouseInventoryFlowProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [chartHeight, setChartHeight] = useState(0)
  const [mode, setMode] = useState<FlowMode>('qty')
  const [scope, setScope] = useState<FlowScope>('cumulative')
  const [flowMonth, setFlowMonth] = useState('')
  const [chartView, setChartView] = useState<FlowChartView>('waterfall')
  const [searchQuery, setSearchQuery] = useState('')
  const [hover, setHover] = useState<HoverTip | null>(null)
  const [cardHoverSku, setCardHoverSku] = useState<string | null>(null)

  const activeLedgerRows = useMemo(() => filterActiveLedgerRows(ledgerRows), [ledgerRows])

  const flowMonths = useMemo(() => {
    const fromLedger = collectLedgerFlowMonths(ledgerRows)
    return fromLedger.length > 0 ? fromLedger : availableMonths
  }, [availableMonths, ledgerRows])

  useEffect(() => {
    if (flowMonths.length === 0) {
      setFlowMonth('')
      return
    }
    setFlowMonth((current) =>
      current && flowMonths.includes(current) ? current : (flowMonths.at(-1) ?? '')
    )
  }, [flowMonths])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const updateSize = () => {
      const nextWidth = el.clientWidth
      const nextHeight = el.clientHeight
      if (nextWidth > 0) {
        setWidth(Math.max(nextWidth, MIN_WIDTH))
      }
      if (nextHeight > 0) {
        setChartHeight(nextHeight)
      }
    }

    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const nextWidth = el.clientWidth
    const nextHeight = el.clientHeight
    if (nextWidth > 0) {
      setWidth(Math.max(nextWidth, MIN_WIDTH))
    }
    if (nextHeight > 0) {
      setChartHeight(nextHeight)
    }
  }, [scope])

  const productNames = useMemo(
    () => buildProductNameMapFromLedger(activeLedgerRows, skuDisplayNames),
    [activeLedgerRows, skuDisplayNames]
  )
  const stockBySku = useMemo(
    () => catalogStockToFlowRows(catalogStock, activeLedgerRows),
    [activeLedgerRows, catalogStock]
  )

  const displayColorMap = useMemo(() => {
    const skus = new Set<string>()
    for (const row of stockBySku) skus.add(row.bulk_sku)
    for (const row of activeLedgerRows) {
      if (row.bk_sku) skus.add(row.bk_sku)
    }
    return buildSkuColorMap([...skus])
  }, [activeLedgerRows, stockBySku])

  const periodFlowModel = useMemo(
    () => buildPeriodFlowModel(activeLedgerRows, scope, flowMonth, mode),
    [activeLedgerRows, flowMonth, mode, scope]
  )

  const monthInflowCount = useMemo(
    () => (flowMonth ? filterLedgerInflowsForMonth(activeLedgerRows, flowMonth).length : 0),
    [activeLedgerRows, flowMonth]
  )
  const monthOutflowCount = useMemo(() => {
    if (!flowMonth) return 0
    return filterLedgerOutflowsForMonth(activeLedgerRows, flowMonth).length
  }, [activeLedgerRows, flowMonth])

  const ledgerInflowTxnCount = useMemo(
    () => activeLedgerRows.filter(isLedgerInflow).length,
    [activeLedgerRows]
  )

  const ledgerOutflowTxnCount = useMemo(
    () => activeLedgerRows.filter(isLedgerOutflow).length,
    [activeLedgerRows]
  )

  const adjustmentSummary = useMemo(
    () =>
      summarizeLedgerAdjustments(
        activeLedgerRows,
        scope === 'month' ? flowMonth || null : null
      ),
    [activeLedgerRows, flowMonth, scope]
  )

  const flowMonthLabel = flowMonth ? formatMonthLabel(flowMonth) : '—'

  const graph = useMemo(
    () => buildMovementFlowGraph(periodFlowModel, mode, scope, productNames, displayColorMap),
    [displayColorMap, mode, periodFlowModel, productNames, scope]
  )

  const hasPriorStockSources = useMemo(
    () => graph.nodes.some((node) => node.sourceKind === 'prior_stock'),
    [graph.nodes]
  )
  const sourceColumnHeader = hasPriorStockSources ? 'Sources' : 'Purchases'
  const height = chartHeight
  const chartHeightSufficient = height >= MIN_CHART_HEIGHT
  const chartHeightTooSmall = height > 0 && height < MIN_CHART_HEIGHT
  const chartLeft = LEFT_MARGIN
  const chartRight = width - RIGHT_MARGIN
  const warehouseHeaderX = (chartLeft + chartRight) / 2
  const vendorsHeaderX = chartLeft / 2
  const storesHeaderX = (chartRight + width) / 2

  const flowTotals = useMemo(() => {
    let totalIn = 0
    let totalOut = 0
    for (const row of periodFlowModel) {
      totalIn += flowMetric(row.purchases, mode)
      if (scope === 'month') {
        const drawTotal = row.storeDraws.reduce((sum, draw) => sum + drawMetric(draw, mode), 0)
        const purchases = flowMetric(row.purchases, mode)
        if (drawTotal > purchases) totalIn += drawTotal - purchases
      }
      for (const draw of row.storeDraws) {
        totalOut += drawMetric(draw, mode)
      }
    }
    return { totalIn, totalOut }
  }, [mode, periodFlowModel, scope])

  const layout = useMemo(() => {
    if (graph.links.length === 0) return null

    const chartTop = 32
    const chartBottom = height - 8

    const generator = sankey<SankeyNodeExtra, SankeyLinkInput>()
      .nodeId((d) => d.nodeId)
      .nodeWidth(NODE_WIDTH)
      .nodePadding(18)
      .extent([
        [chartLeft, chartTop],
        [chartRight, chartBottom],
      ])
      .nodeAlign(sankeyLeft)
      .nodeSort((a, b) => {
        const nodeA = a as SankeyNodeExtra
        const nodeB = b as SankeyNodeExtra
        const skuA = nodeA.bulkSku ?? nodeA.name
        const skuB = nodeB.bulkSku ?? nodeB.name
        const bySku = skuA.localeCompare(skuB)
        if (bySku !== 0) return bySku
        return (nodeA.sourceKind ?? nodeA.name).localeCompare(nodeB.sourceKind ?? nodeB.name)
      })
      .linkSort((a, b) => {
        const linkA = a as SankeyLinkInput
        const linkB = b as SankeyLinkInput
        const skuA = linkA.bulkSku ?? ''
        const skuB = linkB.bulkSku ?? ''
        const bySku = skuA.localeCompare(skuB)
        if (bySku !== 0) return bySku
        const bySource = (linkA.sourceKind ?? '').localeCompare(linkB.sourceKind ?? '')
        if (bySource !== 0) return bySource
        return (a.value ?? 0) - (b.value ?? 0)
      })

    const result = generator({
      nodes: graph.nodes.map((n) => ({ ...n })),
      links: graph.links.map((l) => ({ ...l })),
    })

    pinColumns(result.nodes as LayoutNode[], chartLeft, chartRight, chartTop, chartBottom)
    return result
  }, [chartLeft, chartRight, graph, height])

  const totalInflow = flowTotals.totalIn
  const totalOutflow = flowTotals.totalOut
  const hasOutflows = periodFlowModel.some((row) => row.storeDraws.length > 0)
  const highlightedSku = cardHoverSku ?? hover?.bulkSku ?? null

  const pathGen = sankeyLinkHorizontal()

  const sourceNodeLabels = useMemo(() => {
    if (!layout) return []
    return layout.nodes
      .map((node) => {
        const extra = node as SankeyNodeExtra
        if (extra.column !== 'source') return null
        const y0 = node.y0 ?? 0
        const y1 = node.y1 ?? y0
        return {
          key: `${extra.sourceKind ?? 'source'}:${extra.bulkSku ?? extra.name}`,
          bulkSku: extra.bulkSku,
          label: extra.name,
          midY: (y0 + y1) / 2,
          color: extra.color,
        }
      })
      .filter((group): group is NonNullable<typeof group> => group != null)
  }, [layout])

  const updateHover = (
    event: MouseEvent<SVGPathElement>,
    label: string,
    bulkSku: string
  ) => {
    const chart = chartRef.current
    if (!chart) return
    const rect = chart.getBoundingClientRect()
    setHover({
      label,
      bulkSku,
      x: event.clientX - rect.left + 12,
      y: event.clientY - rect.top + 12,
    })
  }

  const opacityForSku = (bulkSku?: string) => {
    if (!highlightedSku) return 1
    return bulkSku === highlightedSku ? 1 : 0.15
  }

  const cumulativeRangeLabel = useMemo(() => {
    const sorted = collectLedgerFlowMonths(ledgerRows)
    if (sorted.length === 0) {
      if (availableMonths.length === 0) return '—'
      if (availableMonths.length === 1) return availableMonths[0]!
      return `${availableMonths[0]} thru ${availableMonths[availableMonths.length - 1]}`
    }
    if (sorted.length === 1) return sorted[0]!
    return `${sorted[0]} thru ${sorted[sorted.length - 1]}`
  }, [availableMonths, ledgerRows])

  const scopeLabel =
    scope === 'cumulative' ? cumulativeRangeLabel : flowMonth ? flowMonth : '—'

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="shrink-0 space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="container-title">Current warehouse stock</h3>
        </div>
        {stockBySku.length === 0 ? (
          <p className="text-sm text-gray-500">No catalog stock from ledger transactions yet.</p>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {stockBySku.map((row) => {
              const active = highlightedSku === row.bulk_sku
              return (
                <div
                  key={row.bulk_sku}
                  className={`w-44 shrink-0 cursor-pointer rounded-lg border px-3 py-2 transition-colors ${
                    active
                      ? 'border-gray-900 bg-white shadow-sm'
                      : 'border-gray-200 bg-gray-50/70 hover:border-gray-300'
                  }`}
                  onMouseEnter={() => setCardHoverSku(row.bulk_sku)}
                  onMouseLeave={() => setCardHoverSku(null)}
                  onClick={() => setSearchQuery(row.bulk_sku)}
                >
                  <p className="truncate text-xs font-semibold text-gray-900" title={skuDisplayLabel(row.bulk_sku, row.product)}>
                    {skuDisplayLabel(row.bulk_sku, row.product)}
                  </p>
                  <p className="text-xs text-gray-600">
                    {formatQty(row.qty_remaining)} cases
                    <span className="text-gray-400"> · </span>
                    {formatMoney(row.inventory_value)}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    avg {formatMoney(row.weighted_unit_cost)}/case
                  </p>
                  <span
                    className="mt-1 inline-block h-1 w-8 rounded-full"
                    style={{ backgroundColor: displayColorMap.get(row.bulk_sku) ?? '#94a3b8' }}
                    aria-hidden="true"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col space-y-2">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
          <h3 className="container-title">
            Inventory flow
            <span className="ml-1 font-normal text-gray-500">({scopeLabel})</span>
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex h-8 items-center gap-0.5 rounded-lg border border-gray-200 p-0.5">
              <button
                type="button"
                className={`flex h-7 items-center rounded-md px-2.5 text-xs font-medium ${
                  chartView === 'waterfall'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setChartView('waterfall')}
              >
                Waterfall
              </button>
              <button
                type="button"
                className={`flex h-7 items-center rounded-md px-2.5 text-xs font-medium ${
                  chartView === 'sankey'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setChartView('sankey')}
              >
                Sankey
              </button>
            </div>
            <div className="flex h-8 items-center gap-2">
              <div className="flex h-full items-center gap-0.5 rounded-lg border border-gray-200 p-0.5">
                <button
                  type="button"
                  className={`flex h-7 items-center rounded-md px-2.5 text-xs font-medium ${
                    scope === 'cumulative'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setScope('cumulative')}
                >
                  Cumulative
                </button>
                <button
                  type="button"
                  className={`flex h-7 items-center rounded-md px-2.5 text-xs font-medium ${
                    scope === 'month'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setScope('month')}
                >
                  Single month
                </button>
              </div>
              {scope === 'month' ? (
                <select
                  className="h-8 max-w-[10rem] rounded-lg border border-gray-200 bg-white px-2 text-xs font-medium text-gray-700 focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300"
                  value={flowMonth}
                  disabled={flowMonths.length === 0}
                  onChange={(e) => setFlowMonth(e.target.value)}
                  aria-label="Flow month"
                >
                  {flowMonths.length === 0 ? (
                    <option value="">No months</option>
                  ) : (
                    flowMonths.map((month) => (
                      <option key={month} value={month}>
                        {formatMonthLabel(month)}
                      </option>
                    ))
                  )}
                </select>
              ) : null}
            </div>
            <div className="flex h-8 items-center gap-0.5 rounded-lg border border-gray-200 p-0.5">
              <button
                type="button"
                className={`flex h-7 items-center rounded-md px-2.5 text-xs font-medium ${
                  mode === 'qty'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setMode('qty')}
              >
                Cases
              </button>
              <button
                type="button"
                className={`flex h-7 items-center rounded-md px-2.5 text-xs font-medium ${
                  mode === 'dollar'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setMode('dollar')}
              >
                $
              </button>
            </div>
          </div>
        </div>

        {scope === 'month' && !hasOutflows && chartView === 'sankey' ? (
          <p className="text-xs text-gray-500">
            No store outflows for {flowMonthLabel} yet. Store draws appear when{' '}
            <span className="font-medium">store_draw</span> ledger transactions are recorded.
          </p>
        ) : null}

        {adjustmentSummary.count > 0 && chartView === 'sankey' ? (
          <p className="text-xs text-gray-500">
            {adjustmentSummary.count} adjustment{adjustmentSummary.count === 1 ? '' : 's'} (+
            {formatQty(adjustmentSummary.positiveCases)} / −
            {formatQty(adjustmentSummary.negativeCases)} cases) omitted from sankey — inventory
            corrections appear on the waterfall view.
          </p>
        ) : null}

        <div
          ref={containerRef}
          className="flex min-h-0 flex-1 flex-col overflow-hidden w-full"
        >
        {chartView === 'waterfall' ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white p-4">
            <WarehouseInventoryWaterfall
              rows={periodFlowModel}
              mode={mode}
              scope={scope}
              productNames={productNames}
              highlightedSku={highlightedSku}
              onHighlightSku={setCardHoverSku}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
            />
          </div>
        ) : layout ? (
          chartHeightSufficient ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white p-4">
            <div ref={chartRef} className="relative min-h-0 flex-1 overflow-x-auto">
                <svg
                  key={`${scope}-${mode}`}
                  viewBox={`0 0 ${width} ${height}`}
                  height={height}
                  className="block w-full"
                  role="img"
                  aria-label="Warehouse inventory flow sankey"
                >


                  <text
                    x={vendorsHeaderX}
                    y={18}
                    textAnchor="middle"
                    fill="#374151"
                    fontSize={13}
                    fontWeight={600}
                    style={{ fontFamily: 'inherit' }}
                  >
                    {sourceColumnHeader}
                  </text>
                  <text
                    x={warehouseHeaderX}
                    y={18}
                    textAnchor="middle"
                    fill="#374151"
                    fontSize={13}
                    fontWeight={600}
                    style={{ fontFamily: 'inherit' }}
                  >
                    Warehouse
                  </text>
                  <text
                    x={storesHeaderX}
                    y={18}
                    textAnchor="middle"
                    fill="#374151"
                    fontSize={13}
                    fontWeight={600}
                    style={{ fontFamily: 'inherit' }}
                  >
                    Stores
                  </text>

                  {layout.links.map((link, i) => {
                    const d = pathGen(link)
                    if (!d) return null
                    const input = link as typeof link & SankeyLinkInput
                    const bulkSku = input.bulkSku ?? ''
                    const label = input.label ?? 'Flow'
                    const strokeWidth = Math.max(1, link.width ?? 1)
                    const opacity = opacityForSku(bulkSku)
                    const isActive = Boolean(highlightedSku && bulkSku === highlightedSku)
                    return (
                      <g key={i} opacity={opacity} className="transition-opacity duration-150">
                        <path
                          d={d}
                          fill="none"
                          stroke="transparent"
                          strokeWidth={Math.max(strokeWidth, 14)}
                          className="cursor-pointer"
                          onMouseEnter={(event) => updateHover(event, label, bulkSku)}
                          onMouseMove={(event) => updateHover(event, label, bulkSku)}
                          onMouseLeave={() => setHover(null)}
                        />
                        <path
                          d={d}
                          fill="none"
                          stroke={input.color ?? '#94a3b8'}
                          strokeOpacity={isActive ? 0.75 : 0.45}
                          strokeWidth={strokeWidth}
                          className="pointer-events-none"
                        />
                      </g>
                    )
                  })}

                  {layout.nodes.map((node, i) => {
                    const extra = node as SankeyNodeExtra
                    const nodeWidth = Math.max(1, (node.x1 ?? 0) - (node.x0 ?? 0))
                    const nodeHeight = Math.max(1, (node.y1 ?? 0) - (node.y0 ?? 0))
                    const y0 = node.y0 ?? 0
                    const x0 = node.x0 ?? 0
                    const x1 = node.x1 ?? 0
                    const opacity =
                      extra.column === 'store'
                        ? highlightedSku
                          ? 0.55
                          : 1
                        : opacityForSku(extra.bulkSku)

                    return (
                      <g key={i} opacity={opacity} className="transition-opacity duration-150">
                        <rect
                          x={x0}
                          y={y0}
                          width={nodeWidth}
                          height={nodeHeight}
                          fill={extra.color ?? '#64748b'}
                          rx={2}
                        />
                        {extra.column === 'store' && !extra.hideLabel ? (
                          <text
                            x={x1 + 6}
                            y={y0 + nodeHeight / 2}
                            dy="0.35em"
                            textAnchor="start"
                            className="fill-gray-700 text-[10px]"
                          >
                            {extra.name}
                          </text>
                        ) : null}
                      </g>
                    )
                  })}

                  {sourceNodeLabels.map((group) => (
                    <foreignObject
                      key={`source-label-${group.key}`}
                      x={0}
                      y={Math.max(0, group.midY - 22)}
                      width={LEFT_MARGIN - 10}
                      height={44}
                      opacity={opacityForSku(group.bulkSku)}
                      className="transition-opacity duration-150"
                    >
                      <div
                        className="flex h-full items-center justify-end pr-2 text-right text-[10px] leading-tight text-gray-700"
                        title={group.label}
                      >
                        <span className="line-clamp-3 break-words">{group.label}</span>
                      </div>
                    </foreignObject>
                  ))}
                </svg>

              {hover && !cardHoverSku ? (
                <div
                  className="pointer-events-none absolute z-10 max-w-xs rounded-md border border-gray-200 bg-white px-2.5 py-1.5 shadow-md"
                  style={{ left: hover.x, top: hover.y }}
                >
                  <p className="text-xs font-medium text-gray-900">{hover.label}</p>
                </div>
              ) : null}
            </div>

            <p className="mt-2 shrink-0 text-xs text-gray-500">
              {mode === 'qty'
                ? `In: ${formatQty(totalInflow)} cases · Out: ${formatQty(totalOutflow)} cases`
                : `In: ${formatMoney(totalInflow)} · Out: ${formatMoney(totalOutflow)}`}
              {scope === 'month'
                ? ` · ${monthInflowCount} inflow txn(s) · ${monthOutflowCount} outflow txn(s) this month`
                : ` · ${ledgerInflowTxnCount} inflow txn(s) · ${ledgerOutflowTxnCount} outflow txn(s) all-time`}
            </p>
          </div>
          ) : chartHeightTooSmall ? (
            <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-6 text-center">
              <p className="max-w-sm text-sm text-gray-600">
                Increase the height of your browser window to see the Sankey chart.
              </p>
            </div>
          ) : null
        ) : (
          <p className="text-sm text-gray-500">
            No warehouse flow to chart for this scope
            {scope === 'month' ? ` (${flowMonthLabel})` : ''}.
          </p>
        )}
        </div>
      </div>
    </div>
  )
}
